import styled, { css, keyframes } from 'styled-components'
import { Button } from '@sainsburys-tech/bolt'
import { InstagramLogo } from '@sainsburys-tech/bolt-icons'
import { createCSSObject } from '../../../helpers/theming/utilities'

const spacing = (props) => props.theme.size.spacing.lg

const backgroundPulse = (props) => keyframes`
  0% {
    background-color: ${props.theme.color.monochrome.lighter};
  }
  50% {
    background-color: darken(${props.theme.color.monochrome.lighter}, 5);
  }
  100% {
    background-color: ${props.theme.color.monochrome.lighter};
  }
`

const columns = {
  md: 4,
  sm: 3,
  xs: 2
}

const ImageLayout = (props) => css`
  width: calc(100% - ${spacing(props)});
  height: calc(100% - ${spacing(props)});
  margin: ${parseInt(spacing(props), 10) / 2}px;
  position: absolute;
`

const ImageContainer = styled.div`
  padding: ${(props) => `calc(${spacing(props)} / 2)`};

  &:after {
    content: '';
    clear: both;
    display: block;
  }
`

const ImageItem = styled.button`
  all: unset;
  float: left;
  position: relative;
  width: calc(100% / ${columns.xs});
  padding-bottom: calc(100% / ${columns.xs});

  ${(props) =>
    css`
      @media only screen and (min-width: ${props.theme.size.breakpoint['min-width'].sm}) {
        width: calc(100% / ${columns.sm});
        padding-bottom: calc(100% / ${columns.sm});
      }

      ${props.type !== 'mini' &&
      css`
        @media only screen and (min-width: ${props.theme.size.breakpoint['min-width'].md}) {
          width: calc(100% / ${columns.md});
          padding-bottom: calc(100% / ${columns.md});
        }
      `}

      img {
        ${ImageLayout(props)}
        animation: 1s ${backgroundPulse} infinite;
        background-color: ${props.theme.color.monochrome.lighter};
        background-size: cover;
        object-fit: cover;
        cursor: pointer;
      }
    `}

  :focus {
    outline: 5px auto -webkit-focus-ring-color;
  }
`

const CTAblock = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  ${(props) =>
    css`
      background-color: ${props.theme.color.monochrome.lighter};
      padding: ${spacing(props)};
      ${ImageLayout(props)}

      @media only screen and (min-width: ${props.theme.size.breakpoint['min-width'].sm}) {
        padding: ${parseInt(spacing(props), 10) / 2}px;
      }
    `}
`

const PrimaryText = styled.div`
  ${(props) => createCSSObject(props.theme.font.stack['display-1'])}
`

const QuickviewImage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  position: relative;
  margin-bottom: ${(props) => spacing(props)};

  img {
    width: 100%;
    max-height: 500px;
    object-fit: cover;
  }
`

const ViewMore = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${(props) => parseInt(spacing(props), 10) / 2}px;
`

const FilterTags = styled.div`
  display: inline-flex;
`

const TagContainer = styled.div`
  overflow-y: hidden;
  overflow-x: auto;
  padding-bottom: ${(props) => spacing(props)};
`

const OopsMessage = styled.div`
  margin: ${(props) => `${parseInt(spacing(props), 10) * 2}px ${parseInt(spacing(props), 10) * 3}px`};
  text-align: center;
`
const GalleryContainer = styled.div`
  margin-top: ${(props) => spacing(props)};
  display: flex;
  flex-wrap: wrap;
  align-items: center;
`

const ProductContainer = styled.div`
  cursor: pointer;
  margin: ${(props) => spacing(props)};
`

const PanelContainer = styled.a`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  width: 100%;
`

const Thumbnail = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: ${(props) => (props.condensed ? '15px' : '20px')};
  position: relative;
  flex-shrink: 0;

  img {
    margin-top: 5px;
  }
`

const Details = styled.div`
  width: 100%;

  ${(props) =>
    css`
      @media screen and (min-width: ${props.theme.size.breakpoint['min-width'].sm}) {
        width: 100%;
      }
    `};
`
const Name = styled.p`
  && {
    font-weight: ${(props) => props.theme.font.weight.bold};
  }
`

const MoreDetails = styled.div`
  margin-top: 5px;
  width: 100%;
  text-align: right;

  ${(props) =>
    css`
      @media screen and (max-width: ${props.theme.size.breakpoint['max-width'].xs}) {
        text-align: left;
        right: 0;
        bottom: 10px;
      }
    `};
`

const LargerButton = styled(Button)`
  min-width: 108px;
`

const DescriptionContainer = styled.div`
  ${(props) =>
    css`
      padding: 0 ${props.theme.size.spacing.xl} ${props.theme.size.spacing.xl};
    `}
  white-space: pre-wrap;
`

const HeadingContainer = styled.div`
  display: flex;
  align-items: center;
  align-content: center;
`

const InstagramIconLink = styled.a`
  ${(props) => css`
    padding-left: ${props.theme.size.spacing.md};
    padding-top: ${props.theme.size.spacing.md};
  `}
`

const InstagramIcon = styled(InstagramLogo)`
  path {
    fill: ${(props) => props.theme.color.monochrome.dark};
  }
  :hover {
    path {
      fill: ${(props) => props.theme.color.palette['brand-1']};
    }
  }
`

export {
  CTAblock,
  Details,
  ImageContainer,
  ImageItem,
  Name,
  MoreDetails,
  PrimaryText,
  QuickviewImage,
  ViewMore,
  FilterTags,
  PanelContainer,
  TagContainer,
  OopsMessage,
  GalleryContainer,
  ProductContainer,
  Thumbnail,
  LargerButton,
  DescriptionContainer,
  HeadingContainer,
  InstagramIconLink,
  InstagramIcon
}
